import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCollectionsV1Service, serviceName } from '../service';
import { ListPatientBalancesIO, ServiceQueries } from '../types';
import { useCollectionsV1Queries } from './use-collections-v1-queries';
import { UseCollectionsV1QueryEndpointArgs, useCollectionsV1Query } from './use-collections-v1-query';

/**
 * A hook that returns a query for the `ListPatientBalances` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListPatientBalancesQuery = <E = unknown, D = ListPatientBalancesIO['output']>(
  args: UseCollectionsV1QueryEndpointArgs<'ListPatientBalances', E, D>
) =>
  useCollectionsV1Query({
    endpointName: 'ListPatientBalances',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListPatientBalances` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListPatientBalancesQueries = <E = unknown, D = ListPatientBalancesIO['output']>(
  argsArr: UseCollectionsV1QueryEndpointArgs<'ListPatientBalances', E, D>[]
) =>
  useCollectionsV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListPatientBalances',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListPatientBalances` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListPatientBalances = () => {
  const queryClient = useQueryClient();

  const fetchListPatientBalances = async <E = unknown, D = ListPatientBalancesIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCollectionsV1QueryEndpointArgs<'ListPatientBalances', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListPatientBalancesIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListPatientBalances'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListPatientBalancesIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListPatientBalances'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListPatientBalances'>({
          serviceName,
          endpointName: 'ListPatientBalances',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCollectionsV1Service.ListPatientBalances(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListPatientBalances;
};
